import type { ClientTlsCertificateEntry } from '../../../store/proxy-store';

export type CertificateFormat = 'pem' | 'pfx';

export type CertificateFileTarget = 'cert' | 'key' | 'pfx';

export type ManualFormState = {
  name: string;
  origin: string;
  format: CertificateFormat;
  passphrase: string;
  certText?: string;
  certFileName?: string;
  keyText?: string;
  keyFileName?: string;
  pfxBase64?: string;
  pfxFileName?: string;
};

export type ManualFormValidation = {
  trimmedOrigin: string;
  hasOriginScheme: boolean;
  isValid: boolean;
};

export const EMPTY_MANUAL_FORM: ManualFormState = {
  name: '',
  origin: '',
  format: 'pem',
  passphrase: '',
};

export const CERTIFICATE_ACCEPT_BY_TARGET: Record<
  CertificateFileTarget,
  string
> = {
  cert: '.pem,.crt,.cer,application/x-pem-file,application/x-x509-ca-cert',
  key: '.key,.pem,application/x-pem-file',
  pfx: '.p12,.pfx,application/x-pkcs12',
};

export const MAX_CERTIFICATE_FILE_BYTES = 5 * 1024 * 1024;

const ORIGIN_SCHEME_PATTERN = /^https?:\/\//i;

export const toCertificateFormat = (value: string | null): CertificateFormat =>
  value === 'pfx' ? 'pfx' : 'pem';

export const validateManualForm = (
  form: ManualFormState,
): ManualFormValidation => {
  const trimmedOrigin = form.origin.trim();
  const hasOriginScheme = ORIGIN_SCHEME_PATTERN.test(trimmedOrigin);
  const hasIdentity = Boolean(
    form.name.trim() && trimmedOrigin && hasOriginScheme,
  );
  const hasFiles =
    form.format === 'pem'
      ? Boolean(form.certText?.trim() && form.keyText?.trim())
      : Boolean(form.pfxBase64?.trim());

  return { trimmedOrigin, hasOriginScheme, isValid: hasIdentity && hasFiles };
};

export const switchCertificateFormat = ({
  form,
  format,
}: {
  form: ManualFormState;
  format: CertificateFormat;
}): ManualFormState => ({
  ...form,
  format,
  certText: format === 'pem' ? form.certText : undefined,
  certFileName: format === 'pem' ? form.certFileName : undefined,
  keyText: format === 'pem' ? form.keyText : undefined,
  keyFileName: format === 'pem' ? form.keyFileName : undefined,
  pfxBase64: format === 'pfx' ? form.pfxBase64 : undefined,
  pfxFileName: format === 'pfx' ? form.pfxFileName : undefined,
});

export const setCertificateFile = ({
  form,
  target,
  content,
  fileName,
}: {
  form: ManualFormState;
  target: CertificateFileTarget;
  content: string | undefined;
  fileName: string | undefined;
}): ManualFormState => {
  if (target === 'pfx') {
    return { ...form, pfxBase64: content, pfxFileName: fileName };
  }

  if (target === 'cert') {
    return { ...form, certText: content, certFileName: fileName };
  }

  return { ...form, keyText: content, keyFileName: fileName };
};

export const buildManualCertificateEntry = ({
  form,
  id,
}: {
  form: ManualFormState;
  id: string;
}): ClientTlsCertificateEntry => ({
  id,
  name: form.name.trim(),
  origin: form.origin.trim(),
  cert: form.format === 'pem' ? form.certText?.trim() : undefined,
  key: form.format === 'pem' ? form.keyText?.trim() : undefined,
  pfx: form.format === 'pfx' ? form.pfxBase64?.trim() : undefined,
  passphrase: form.passphrase.trim() || undefined,
  enabled: true,
  source: 'manual',
});

export const readFileAsBase64 = (file: File) =>
  new Promise<string>((resolvePromise, rejectPromise) => {
    const reader = new FileReader();

    reader.onload = () => {
      const result = reader.result;

      if (typeof result !== 'string') {
        rejectPromise(new Error('Unexpected file reader result'));

        return;
      }

      const commaIndex = result.indexOf(',');

      resolvePromise(commaIndex >= 0 ? result.slice(commaIndex + 1) : result);
    };

    reader.onerror = () =>
      rejectPromise(reader.error ?? new Error('Read error'));
    reader.readAsDataURL(file);
  });
